import React, { useEffect } from "react";
import LocalizedText from '../../presentational/LocalizedText';
import useTranslate from "../../../hooks/useTranslate";
import Input from "../../presentational/Input";
import Button from "../../presentational/Button";
import { useResendCodeTimer } from "./hooks";
import './VerifyPhone.css'
import { useTurnstile } from "../../../hooks/useTurnstile";

interface VerifyPhoneProps {
  serverError: string;
  isLoading: boolean;
  emailOrPhone: string;
  resendWait: number;
  verificationCode: string;
  attemptVerifyPhone: (verificationCode: string) => void;
  resendVerificationCode: () => Promise<void>;
  setVerificationCode: (verificationCode: string) => void;
}

const VerifyPhone: React.FC<VerifyPhoneProps> = ({
  serverError,
  isLoading,
  emailOrPhone,
  resendWait,
  verificationCode,
  attemptVerifyPhone,
  resendVerificationCode,
  setVerificationCode,
}) => {
  const {
    onResendCode,
    waitCountdown,
    isCodeResent,
    isResendCodeReady,
  } = useResendCodeTimer(resendVerificationCode, resendWait)

  const translations = useTranslate(["participant-login-verification-code-label", "participant-login-next-label"])
  useTurnstile();

  const handleSubmit = () => {
    attemptVerifyPhone(verificationCode)
    return false
  }

  const handleVerificationCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setVerificationCode(e.target.value)
  }

  return (
    <form className="login-action verify-phone" onSubmit={handleSubmit}>
      <h3><LocalizedText textKey="participant-login-verify-phone-title" /></h3>
      <p className="instructions"><LocalizedText textKey="participant-login-verify-phone-info" values={{ emailOrPhone: emailOrPhone }} /></p>
      {
        serverError && (
          <p className="server-error">{serverError}</p>
        )
      }
      <div className="field-group">
        <label className="login-field" htmlFor="phoneVerificationCode">{translations["participant-login-verification-code-label"]}</label>
      
        <Input
          autoComplete="one-time-code"
          autoFocus={true}
          className="autofocus"
          disabled={isLoading}
          onChange={handleVerificationCodeChange}
          id="phoneVerificationCode"
          type="tel"
          value={verificationCode}
          placeholder="123456"
          fullWidth={true}
          htmlAttributes={{
            maxLength: 6,
            pattern: "[0-9]{6}",
            spellCheck: false,
            autoCorrect: "off",
            autoCapitalize: "none",
          }}
        />
      </div>
      <div>
        <div id="cf-turnstile" className="cf-turnstile"></div>
        <Button
          onClick={handleSubmit}
          disabled={!verificationCode}
          className="primary-action"
          width="100%"
          intent='primary'
          loading={isLoading}
          type='submit'
        >
          <span dangerouslySetInnerHTML={{ __html: translations["participant-login-next-label"] }} />
        </Button>
      </div>
      <div aria-live="polite">
        {
          isResendCodeReady && (
            <p className="sub-action">
              <Button
                onClick={onResendCode}
                intent='secondary'
                disabled={isLoading}
              >
                <LocalizedText textKey="participant-login-resend-code-label" />
              </Button>
            </p>
          )
        }
        {
          isCodeResent && !isResendCodeReady && (
            <p className="sub-action"><LocalizedText textKey="participant-login-verify-code-resent" /></p>
          )
        }
        {
          !isResendCodeReady && (
            <p className="sub-action"><LocalizedText textKey="participant-login-verify-resend-available-format" values={{ resendWait: waitCountdown }} /></p>
          )
        }
      </div>
    </form>
  )
}

export default VerifyPhone